import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Icon } from '~/global/components/icon/Icon';
import type { BaseModalProps } from '@/contexts/ModalContext';
import {
  HealthDataSource,
  type RecordedSessionSample,
} from '~/health/lib/health-provider/health-provider';
import type { SessionCandidate } from '~/health/lib/session-matcher/session-matcher';

export interface RecordedSessionMatchSheetProps extends BaseModalProps {
  // Optional to satisfy the ModalContext showModal typing; always supplied at
  // runtime by useRecordedSessionImport.
  candidates?: SessionCandidate[];
  onAttach?: (sample: RecordedSessionSample) => Promise<void>;
  portalHost?: string;
}

const sourceLabelKey = (source: RecordedSessionSample['source']) =>
  source === HealthDataSource.HEALTH_CONNECT
    ? 'recorded.sourceHealthConnect'
    : 'recorded.sourceAppleHealth';

/**
 * The #29 confirm / pick sheet shown once at workout finish. With a single
 * overlapping session it confirms the one match; with several it lets the user
 * pick which to attach (best-overlap pre-selected). Attaching persists a
 * RecordedSession linked to the workout, then closes.
 */
export const RecordedSessionMatchSheet = ({
  candidates,
  onAttach,
  onClose,
  portalHost,
}: RecordedSessionMatchSheetProps) => {
  const { t } = useTranslation('workout');
  const [selected, setSelected] = useState(0);

  const items = candidates ?? [];
  const isSingle = items.length === 1;

  const attachMutation = useMutation({
    mutationFn: async () => {
      const candidate = items[selected];
      if (candidate) await onAttach?.(candidate.session);
    },
    onSuccess: () => onClose(),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent portalHost={portalHost}>
        <View testID="recorded-session-match-sheet" className="gap-4">
          <DialogHeader>
            <DialogTitle>
              {t(
                isSingle
                  ? 'recorded.match.confirmTitle'
                  : 'recorded.match.pickTitle',
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                isSingle
                  ? 'recorded.match.confirmSubtitle'
                  : 'recorded.match.pickSubtitle',
              )}
            </DialogDescription>
          </DialogHeader>

          <View className="gap-2">
            {items.map((candidate, index) => {
              const { session } = candidate;
              const isSelected = index === selected;
              const minutes = Math.round(
                (session.endedAt.getTime() - session.startedAt.getTime()) /
                  60000,
              );
              return (
                <Pressable
                  key={session.externalId}
                  testID={`recorded-session-candidate-${index}`}
                  onPress={() => setSelected(index)}
                  disabled={isSingle}
                  className={cn(
                    'flex-row items-center gap-3 rounded-lg border bg-surface-elevated p-3',
                    isSelected ? 'border-accent' : 'border-transparent',
                  )}
                >
                  <View className="h-9 w-9 items-center justify-center rounded-lg bg-accent-soft">
                    <Icon name="HeartPulse" size={18} className="text-accent" />
                  </View>
                  <View className="flex-1 gap-0.5">
                    <Text className="text-sm font-semibold text-text-primary">
                      {t(sourceLabelKey(session.source))}
                    </Text>
                    <Text className="text-xs text-text-secondary">
                      {t('recorded.match.range', {
                        start: format(session.startedAt, 'HH:mm'),
                        end: format(session.endedAt, 'HH:mm'),
                        minutes,
                      })}
                    </Text>
                  </View>
                  <View className="items-end gap-0.5">
                    {session.avgHeartRate != null && (
                      <Text className="text-xs text-text-secondary">
                        {t('recorded.match.bpm', { bpm: session.avgHeartRate })}
                      </Text>
                    )}
                    {session.activeCalories != null && (
                      <Text className="text-xs text-text-secondary">
                        {t('recorded.match.kcal', {
                          kcal: Math.round(session.activeCalories),
                        })}
                      </Text>
                    )}
                  </View>
                  {!isSingle && (
                    <View
                      className={cn(
                        'h-5 w-5 items-center justify-center rounded-full border',
                        isSelected ? 'border-accent' : 'border-border-strong',
                      )}
                    >
                      {isSelected && (
                        <View className="h-2.5 w-2.5 rounded-full bg-accent" />
                      )}
                    </View>
                  )}
                </Pressable>
              );
            })}
          </View>

          <DialogFooter>
            <Button
              testID="recorded-session-attach-button"
              variant="primary"
              onPress={() => attachMutation.mutate()}
              disabled={attachMutation.isPending}
            >
              <Text className="font-semibold text-accent-text">
                {t(
                  isSingle
                    ? 'recorded.match.attach'
                    : 'recorded.match.attachSelected',
                )}
              </Text>
            </Button>
            <Button
              testID="recorded-session-dismiss-button"
              variant="ghost"
              onPress={() => onClose()}
              disabled={attachMutation.isPending}
            >
              <Text className="font-medium text-text-secondary">
                {t('recorded.match.notNow')}
              </Text>
            </Button>
          </DialogFooter>
        </View>
      </DialogContent>
    </Dialog>
  );
};
